import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Plus, Pencil, Trash2, X, Search, Star, Camera, User, Calendar } from "lucide-react";

import { API_URL as API } from "../../config";
const emptyForm = { name: "", age: "", position: "", jersey_number: "", school: "", grade: "", parent_name: "", parent_phone: "", status: "active", notes: "", skill_rating: "", skill_speed: "", skill_shooting: "", skill_passing: "", skill_defense: "", skill_dribbling: "", joined_date: "" };

const StarRating = ({ value, onChange, size = "w-5 h-5" }) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(i => (
      <button key={i} type="button" onClick={() => onChange?.(i)}
        className={`${onChange ? "cursor-pointer hover:scale-110" : "cursor-default"} transition-transform`}>
        <Star className={`${size} ${i <= (value || 0) ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
      </button>
    ))}
  </div>
);

const ProfilePic = ({ athlete, small }) => {
  if (athlete.profile_picture) {
    return <img src={`${API}/api/files/${athlete.profile_picture}`} alt={athlete.name}
      className={`${small ? "w-10 h-10" : "w-24 h-24"} rounded-full object-cover border-2 border-[#87CEEB]/30`} />;
  }
  return (
    <div className={`${small ? "w-10 h-10" : "w-24 h-24"} rounded-full bg-[#87CEEB]/20 flex items-center justify-center border-2 border-[#87CEEB]/30`}>
      <User className={`${small ? "w-5 h-5" : "w-10 h-10"} text-[#87CEEB]`} />
    </div>
  );
};

export default function AthletesPage() {
  const { authFetch } = useAuth();
  const { t } = useLanguage();
  const [athletes, setAthletes] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showProfile, setShowProfile] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const a = t.admin;

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/api/admin/athletes`);
    if (res.ok) setAthletes(await res.json());
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = { ...form };
    body.age = body.age ? parseInt(body.age) : null;
    body.jersey_number = body.jersey_number ? parseInt(body.jersey_number) : null;
    body.skill_rating = body.skill_rating ? parseInt(body.skill_rating) : null;
    body.skill_speed = body.skill_speed ? parseInt(body.skill_speed) : null;
    body.skill_shooting = body.skill_shooting ? parseInt(body.skill_shooting) : null;
    body.skill_passing = body.skill_passing ? parseInt(body.skill_passing) : null;
    body.skill_defense = body.skill_defense ? parseInt(body.skill_defense) : null;
    body.skill_dribbling = body.skill_dribbling ? parseInt(body.skill_dribbling) : null;
    Object.keys(body).forEach(k => { if (body[k] === "" || body[k] === null) delete body[k]; });
    if (!body.name) return;
    body.status = form.status || "active";
    const url = editId ? `${API}/api/admin/athletes/${editId}` : `${API}/api/admin/athletes`;
    await authFetch(url, { method: editId ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    setShowForm(false); setForm(emptyForm); setEditId(null); load();
  };

  const handleEdit = (a) => {
    setForm({ name: a.name || "", age: a.age?.toString() || "", position: a.position || "", jersey_number: a.jersey_number?.toString() || "", school: a.school || "", grade: a.grade || "", parent_name: a.parent_name || "", parent_phone: a.parent_phone || "", status: a.status || "active", notes: a.notes || "", skill_rating: a.skill_rating?.toString() || "", skill_speed: a.skill_speed?.toString() || "", skill_shooting: a.skill_shooting?.toString() || "", skill_passing: a.skill_passing?.toString() || "", skill_defense: a.skill_defense?.toString() || "", skill_dribbling: a.skill_dribbling?.toString() || "", joined_date: a.joined_date || "" });
    setEditId(a.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this athlete?")) return;
    await authFetch(`${API}/api/admin/athletes/${id}`, { method: "DELETE" }); load();
  };

  const handlePhotoUpload = async (athleteId, file) => {
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    await authFetch(`${API}/api/admin/athletes/${athleteId}/photo`, { method: "PUT", body: fd });
    setUploading(false); load();
    if (showProfile) {
      const res = await authFetch(`${API}/api/admin/athletes/${athleteId}`);
      if (res.ok) setShowProfile(await res.json());
    }
  };

  const filtered = athletes.filter(a => a.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="athletes-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">Athletes</h2>
          <p className="text-gray-500 text-sm">{athletes.length} total athletes</p>
        </div>
        <button onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }} data-testid="add-athlete-btn"
          className="flex items-center gap-2 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black px-5 py-2.5 rounded-xl font-semibold text-sm transition-all">
          <Plus className="w-4 h-4" /> Add Athlete
        </button>
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search athletes..."
          className="w-full sm:w-80 pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" data-testid="search-athletes" />
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{a.player}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden sm:table-cell">{a.position}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">{a.jersey}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">{a.rating}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">{a.joined}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{a.status}</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">{a.actions}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-12 text-center text-gray-400">{athletes.length === 0 ? a.noAthletes : a.noResults}</td></tr>
              ) : filtered.map(a => (
                <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setShowProfile(a)} data-testid={`athlete-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <ProfilePic athlete={a} small />
                      <div>
                        <p className="font-medium text-gray-900">{a.name}</p>
                        <p className="text-xs text-gray-400">Age {a.age || "-"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{a.position || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{a.jersey_number || "-"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell"><StarRating value={a.skill_rating} size="w-3.5 h-3.5" /></td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">{a.joined_date || "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${a.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{a.status || "active"}</span>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <button onClick={() => handleEdit(a)} className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-500 mr-1"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(a.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Profile Modal */}
      {showProfile && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowProfile(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="athlete-profile-modal">
            <div className="p-6 text-center border-b relative">
              <button onClick={() => setShowProfile(null)} className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
              <div className="relative inline-block">
                <ProfilePic athlete={showProfile} />
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-[#87CEEB] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#6bb8d9] transition-colors">
                  <Camera className="w-4 h-4 text-black" />
                  <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handlePhotoUpload(showProfile.id, e.target.files[0])} />
                </label>
              </div>
              {uploading && <p className="text-xs text-[#87CEEB] mt-2">{a.uploading}</p>}
              <h3 className="text-xl font-bold mt-3 font-['Kanit']">{showProfile.name}</h3>
              <p className="text-gray-500 text-sm">#{showProfile.jersey_number || "-"} &middot; {showProfile.position || "Unassigned"}</p>
              <div className="flex items-center justify-center gap-1 mt-2">
                <StarRating value={showProfile.skill_rating} />
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-400">{a.age}</span><p className="font-medium">{showProfile.age || "-"}</p></div>
                <div><span className="text-gray-400">{a.grade}</span><p className="font-medium">{showProfile.grade || "-"}</p></div>
                <div><span className="text-gray-400">{a.school}</span><p className="font-medium">{showProfile.school || "-"}</p></div>
                <div><span className="text-gray-400">{a.status}</span><p className="font-medium capitalize">{showProfile.status}</p></div>
                <div><span className="text-gray-400">{a.joined}</span><p className="font-medium">{showProfile.joined_date || "-"}</p></div>
                <div><span className="text-gray-400">{a.parent}</span><p className="font-medium">{showProfile.parent_name || "-"}</p></div>
              </div>
              {(showProfile.skill_speed || showProfile.skill_shooting || showProfile.skill_passing || showProfile.skill_defense || showProfile.skill_dribbling) && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2 text-sm">{a.skillBreakdown}</h4>
                  <div className="space-y-2">
                    {[{label:a.speed,val:showProfile.skill_speed},{label:a.shooting,val:showProfile.skill_shooting},{label:a.passing,val:showProfile.skill_passing},{label:a.defense,val:showProfile.skill_defense},{label:a.dribbling,val:showProfile.skill_dribbling}].map(s => s.val ? (
                      <div key={s.label} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-16">{s.label}</span>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#87CEEB] rounded-full" style={{width:`${(s.val/5)*100}%`}} />
                        </div>
                        <span className="text-xs font-medium w-4">{s.val}</span>
                      </div>
                    ) : null)}
                  </div>
                </div>
              )}
              {showProfile.notes && <div><h4 className="font-semibold text-gray-700 mb-1 text-sm">{a.notes}</h4><p className="text-sm text-gray-600">{showProfile.notes}</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="athlete-form-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold font-['Kanit']">{editId ? a.editAthlete : a.addAthlete}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">{a.name} *</label>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" data-testid="athlete-name-input" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                  <input type="number" value={form.age} onChange={e => setForm({...form, age: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Jersey #</label>
                  <input type="number" value={form.jersey_number} onChange={e => setForm({...form, jersey_number: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                  <select value={form.position} onChange={e => setForm({...form, position: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]">
                    <option value="">Select...</option><option value="Forward">Forward</option><option value="Midfielder">Midfielder</option><option value="Defender">Defender</option><option value="Goalkeeper">Goalkeeper</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]">
                    <option value="active">Active</option><option value="inactive">Inactive</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Joined Date</label>
                  <input type="date" value={form.joined_date} onChange={e => setForm({...form, joined_date: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">School</label>
                  <input value={form.school} onChange={e => setForm({...form, school: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                  <input value={form.grade} onChange={e => setForm({...form, grade: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Parent</label>
                  <input value={form.parent_name} onChange={e => setForm({...form, parent_name: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Parent Phone</label>
                  <input value={form.parent_phone} onChange={e => setForm({...form, parent_phone: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">{a.overallRating}</label>
                <StarRating value={parseInt(form.skill_rating) || 0} onChange={v => setForm({...form, skill_rating: v.toString()})} /></div>
              <div className="grid grid-cols-5 gap-2">
                {[{k:"skill_speed",l:"SPD"},{k:"skill_shooting",l:"SHT"},{k:"skill_passing",l:"PAS"},{k:"skill_defense",l:"DEF"},{k:"skill_dribbling",l:"DRB"}].map(s => (
                  <div key={s.k} className="text-center"><label className="block text-xs text-gray-500 mb-1">{s.l}</label>
                    <input type="number" min="1" max="5" value={form[s.k]} onChange={e => setForm({...form, [s.k]: e.target.value})} className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:border-[#87CEEB]" /></div>
                ))}
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.notes}</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB] resize-none" /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">{a.cancel}</button>
                <button type="submit" data-testid="athlete-form-submit" className="flex-1 py-2.5 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black rounded-xl text-sm font-bold">{editId ? a.update : a.addAthlete}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
